import ThemeButton from 'components/ui/Button/ThemeButton';
import Text from 'components/ui/Text';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { ImageExtension } from 'microshop-api';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import {
    confirmWithdrawal,
    selectWithdrawal,
    selectWithdrawalSelectedCount,
    withdrawalQuantitySet,
} from 'store/reducers/withdrawalSlice';
import styled from 'styled-components/macro';
import getImage, { ImageType } from 'utils/getImage';
import { WITHDRAWAL_ROUTES } from 'utils/withdrawalRoutes';
import { LineImage, LineInfo, LineRow, Message, SummaryRow } from './WithdrawalStyles';

const WithdrawalSelection = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { order, isWithinWindow, windowEndsAt, selection, confirmStatus } = useAppSelector(selectWithdrawal);
    const selectedCount = useAppSelector(selectWithdrawalSelectedCount);

    if (!order) return <Navigate to={WITHDRAWAL_ROUTES.lookup} replace />;

    const submitting = confirmStatus === 'pending';
    const canConfirm = isWithinWindow && selectedCount > 0 && !submitting;
    const windowEndsOn = windowEndsAt ? windowEndsAt.substring(0, 10) : '';
    const outOfWindowMessage = t('withdrawal_out_of_window');

    const onConfirm = async () => {
        if (!canConfirm) return;
        const result = await dispatch(confirmWithdrawal());
        if (confirmWithdrawal.fulfilled.match(result)) {
            navigate(WITHDRAWAL_ROUTES.success);
            return;
        }
        if (result.payload !== 'expired' && result.payload !== 'invalid' && result.payload !== 'rateLimited')
            navigate(WITHDRAWAL_ROUTES.error);
    };

    return (
        <div>
            <h1>{t('withdrawal_selection_title')}</h1>
            <Text light className="mb-2">
                {t('withdrawal_selection_intro')}
            </Text>
            <Text className="mb-1">{`${t('order_number')}: ${order.orderNumber}`}</Text>
            <Text className="mb-3">{`${t('withdrawal_order_date')}: ${order.orderDate.substring(0, 10)}`}</Text>

            {isWithinWindow ? (
                <Text light className="mb-2">
                    {t('withdrawal_window_ends', { date: windowEndsOn })}
                </Text>
            ) : (
                <Message role="alert">{outOfWindowMessage}</Message>
            )}

            <div>
                {order.lines.map((line) => {
                    const inputId = `withdrawal-quantity-${line.lineId}`;
                    const details = [line.color, line.size, line.split].filter((part) => !!part).join(' / ');
                    return (
                        <LineRow key={line.lineId}>
                            <LineImage
                                alt=""
                                src={getImage(line.image ?? undefined, ImageType.Preview, ImageExtension.Jpg)}
                            />
                            <LineInfo>
                                <Text bold>{line.productName}</Text>
                                {details && <Text light>{details}</Text>}
                                <Text light>
                                    {`${t('withdrawal_ordered')}: ${line.quantity}`}
                                    {line.unitPriceFormatted ? ` × ${line.unitPriceFormatted}` : ''}
                                </Text>
                            </LineInfo>
                            <QuantityField>
                                <label htmlFor={inputId}>{t('quantity')}</label>
                                <input
                                    id={inputId}
                                    className="form-control"
                                    type="number"
                                    inputMode="numeric"
                                    min={0}
                                    max={line.quantity}
                                    step={1}
                                    disabled={!isWithinWindow || submitting}
                                    value={selection[line.lineId] ?? 0}
                                    onChange={(e) =>
                                        dispatch(
                                            withdrawalQuantitySet({
                                                lineId: line.lineId,
                                                quantity: Number.parseInt(e.target.value, 10),
                                            }),
                                        )
                                    }
                                />
                            </QuantityField>
                        </LineRow>
                    );
                })}
            </div>

            {order.totalFormatted && (
                <SummaryRow className="mt-2">
                    <span>{t('withdrawal_order_total')}</span>
                    <span>{order.totalFormatted}</span>
                </SummaryRow>
            )}

            {confirmStatus === 'expired' && <Message role="alert">{outOfWindowMessage}</Message>}
            {confirmStatus === 'invalid' && <Message role="alert">{t('withdrawal_invalid_selection')}</Message>}
            {confirmStatus === 'rateLimited' && <Message role="alert">{t('withdrawal_too_many')}</Message>}

            <span title={!isWithinWindow ? outOfWindowMessage : undefined}>
                <ThemeButton
                    pill={false}
                    className="mt-3"
                    loading={submitting}
                    disabled={!canConfirm}
                    onClick={onConfirm}
                    text={t('withdrawal_confirm')}
                />
            </span>
        </div>
    );
};

const QuantityField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 88px;
    flex-shrink: 0;

    label {
        font-size: 12px;
        margin: 0;
    }
`;

export default WithdrawalSelection;
